import { useEffect, useState } from 'react';
import { C, FONTS } from '../../tokens/design';
import { useClient } from '../../context/ClientContext';
import { useUser } from '../../context/UserContext';
import { useApiClient } from '../../api/apiClient';
import { useOnboardingStatus } from '../../hooks/useClientData';
import Shell from '../../components/layout/Shell';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Skeleton from '../../components/ui/Skeleton';
import AwsOnboardingPanel from '../../components/aws/AwsOnboardingPanel';

const inputStyle = {
  width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 6,
  padding: '9px 11px', color: C.t1, fontSize: 12, fontFamily: FONTS.mono, outline: 'none',
  boxSizing: 'border-box',
};

function parseTenantIds(primary, additional) {
  return [primary, ...String(additional || '').split(/[\s,;]+/)]
    .map(v => v.trim())
    .filter(Boolean)
    .filter((v, i, arr) => arr.findIndex(x => x.toLowerCase() === v.toLowerCase()) === i);
}

function pluralize(count, singular, plural = `${singular}s`) {
  return Number(count) === 1 ? singular : plural;
}

function formatAzureValidationResult(result) {
  if (!result) return 'Azure verification returned no response.';

  const unsupportedScopes = Array.isArray(result.unsupportedTargetScopes) ? result.unsupportedTargetScopes : [];
  const missingTargets = Array.isArray(result.missingTargetSubscriptionIds) ? result.missingTargetSubscriptionIds : [];
  const failedPostureReads = Array.isArray(result.failedSecurityPostureSubscriptionIds) ? result.failedSecurityPostureSubscriptionIds : [];
  const tenants = Array.isArray(result.tenants) ? result.tenants : [];
  const tenantDetails = tenants.map(t => {
    const tenant = t.tenantId || 'tenant';
    const subscriptionCount = t.subscriptionCount || 0;
    const validatedCount = t.validatedSubscriptionCount || 0;
    if (t.armAuthenticated && subscriptionCount === 0) return `${tenant}: ARM token issued; no Azure subscriptions visible (informational)`;
    if (t.scanReady && validatedCount === 0) return `${tenant}: subscription auto-discovery ready; no Azure subscriptions visible`;
    if (t.scanReady) return `${tenant}: ${validatedCount} subscription${Number(validatedCount) === 1 ? '' : 's'} verified for Azure CSPM`;
    if (unsupportedScopes.length > 0) return `${tenant}: unsupported Azure target scope${unsupportedScopes.length === 1 ? '' : 's'} saved`;
    if (missingTargets.length > 0) return `${tenant}: ${missingTargets.length} configured target subscription${missingTargets.length === 1 ? '' : 's'} not visible across configured tenants`;
    if (failedPostureReads.length > 0 || (t.armAuthenticated && t.subscriptionReaderReady && !t.securityPostureReadReady)) return `${tenant}: subscriptions visible, Microsoft.Security posture read missing or incomplete`;
    if (t.armAuthenticated && !t.subscriptionReaderReady) return `${tenant}: ARM token issued but subscription enumeration did not succeed`;
    if (t.armAuthenticated && t.targetScopeCount === 0) return `${tenant}: ARM token issued; subscriptions will be auto-discovered from app visibility`;
    if (t.armAuthenticated) return `${tenant}: ARM token issued, ${subscriptionCount} subscription${Number(subscriptionCount) === 1 ? '' : 's'} visible, but no target subscriptions are scan-ready`;
    return `${tenant}: ARM authentication failed`;
  });
  const suffix = tenantDetails.length ? ` (${tenantDetails.join('; ')})` : '';
  const roles = Array.isArray(result.requiredRoleAssignments) && result.requiredRoleAssignments.length
    ? result.requiredRoleAssignments.join(' + ')
    : 'Reader + Security Reader';
  const scope = result.requiredArmScope || 'https://management.azure.com/.default';

  if (result.scanReady || result.valid) return `Azure ARM access verified for CSPM${suffix}.`;
  if (result.authenticated || result.armAuthenticated) {
    return `Azure app authenticated to ARM (${scope}) but is not CSPM scan-ready${suffix}. Confirm ${roles} on the subscriptions the platform enterprise app should review, or leave subscription targets empty for automatic discovery.`;
  }
  return `Azure verification failed${suffix}. ${result.error || `Confirm Microsoft admin consent, ARM token access (${scope}), and ${roles} assignments for subscriptions that should be reviewed.`}`;
}

function formatAwsValidationResult(result) {
  if (!result) return 'AWS verification returned no response.';

  const details = [];
  if (result.accountId) details.push(`account ${result.accountId}`);
  if (result.expectedAccountId && result.expectedAccountId !== result.accountId) details.push(`expected ${result.expectedAccountId}`);
  if (result.authMode) details.push(result.authMode);
  if (result.regionCount !== undefined && result.regionCount !== null) {
    details.push(`${result.regionCount} ${pluralize(result.regionCount, 'region')}`);
  }
  if (result.arn) details.push(result.arn);

  const suffix = details.length ? ` (${details.join(' / ')})` : '';
  if (result.valid || result.scanReady) return `AWS verified and scan-ready${suffix}.`;
  if (result.authenticated) {
    return `AWS authenticated but is not scan-ready${suffix}. ${result.error || 'Review saved scope and permissions.'}`;
  }
  return `AWS verification failed${suffix}. ${result.error || 'Review saved credentials and permissions.'}`;
}

function CredField({ label, value, onChange, configured, type = 'text', placeholder, autoComplete = 'off', name }) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
        {configured && <span style={{ fontSize: 9, color: C.grn, fontFamily: FONTS.mono }}>Configured {'\u2713'}</span>}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          name={name || label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
          autoComplete={autoComplete}
          spellCheck={false}
          type={isPassword && !show ? 'password' : 'text'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={configured ? 'Configured \u2713 — enter new value to overwrite' : placeholder || ''}
          style={inputStyle}
        />
        {isPassword && (
          <button onClick={() => setShow(!show)} style={{
            position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', color: C.t3, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.mono,
          }}>{show ? 'Hide' : 'Show'}</button>
        )}
      </div>
    </div>
  );
}

function CredSection({ title, icon, fields, onSave, saving, result, collapsible, defaultOpen = true, readOnly = false, extraActions = null }) {
  const [open, setOpen] = useState(defaultOpen);
  const allEmpty = fields.every(f => !f.value);

  return (
    <Card style={{ padding: 20, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: open ? 14 : 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14 }}>{icon}</span>
          <span style={{ fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans, color: C.t1 }}>{title}</span>
          {collapsible && <span style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>Optional</span>}
        </div>
        {collapsible && (
          <button onClick={() => setOpen(!open)} style={{ background: 'none', border: 'none', color: C.t2, cursor: 'pointer', fontSize: 10, fontFamily: FONTS.mono }}>
            {open ? '\u25B4 Collapse' : '\u25BE Expand'}
          </button>
        )}
      </div>

      {open && (
        <>
          {fields.map(f => (
            <CredField
              key={f.key}
              label={f.label}
              value={f.value}
              onChange={f.onChange}
              configured={f.configured}
              type={f.type}
              placeholder={f.placeholder}
              autoComplete={f.autoComplete}
              name={f.name || `sentinel-${f.key.replace(/[^a-z0-9]+/gi, '-')}`}
            />
          ))}

          <div style={{ padding: '8px 10px', borderRadius: 6, background: `${C.amb}08`, border: `1px solid ${C.amb}22`, fontSize: 9, color: C.amb, fontFamily: FONTS.mono, marginBottom: 14 }}>
            Saving new credentials will overwrite existing values. Values are encrypted at rest.
          </div>

          {result && (
            <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 6, fontSize: 11, fontFamily: FONTS.mono, background: result.ok ? `${C.grn}12` : C.redBg, border: `1px solid ${result.ok ? C.grn : C.red}33`, color: result.ok ? C.grn : C.red }}>
              {result.msg}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {extraActions}
            {!readOnly && <Button variant="pri" onClick={onSave} disabled={saving || allEmpty}>
              {saving ? 'Saving...' : `Save ${title}`}
            </Button>}
          </div>
        </>
      )}
    </Card>
  );
}

export function buildAdSecretPairs({ domainFqdn, domainController, serviceAccountUpn, serviceAccountPassword }) {
  const fqdn = String(domainFqdn || '').trim();
  const dc = String(domainController || '').trim();
  const upn = String(serviceAccountUpn || '').trim();
  const password = String(serviceAccountPassword || '');

  return [
    { key: 'ad/domain-fqdn', value: fqdn },
    { key: 'ad/domain-controller', value: dc },
    { key: 'ad/service-account-upn', value: upn },
    { key: 'ad/username', value: upn },
    { key: 'ad/service-account-password', value: password },
    { key: 'ad/password', value: password },
  ].filter(pair => pair.value);
}

export function credentialIsConfigured(onboarding, key) {
  if (!onboarding) return false;
  const status = onboarding.credentialStatus || {};
  const legacy = onboarding.steps || onboarding;
  if (key.startsWith('azure')) return Boolean(status.azureConfigured ?? onboarding.azureConfigured ?? legacy.azureConfigured ?? legacy.azure);
  if (key.startsWith('aws')) return Boolean(status.awsConfigured ?? onboarding.awsConfigured ?? legacy.awsConfigured ?? legacy.aws);
  if (key.startsWith('ad')) return Boolean(status.adConfigured ?? onboarding.adConfigured ?? legacy.adConfigured ?? legacy.ad);
  return false;
}

export default function Credentials() {
  const { clientId } = useClient();
  const { profile, isPlatformAdmin, isMspUser, isClientUser } = useUser();
  const canWrite = isPlatformAdmin || isMspUser;
  const isClientViewer = profile?.role === 'ClientViewer';
  const canUseAwsOnboarding = canWrite || (isClientUser && !isClientViewer);
  const api = useApiClient();
  const { data: onboarding, loading: obLoading, refetch: refetchOnboarding } = useOnboardingStatus();

  // Determine which fields are already configured from onboarding status.
  const configured = (key) => credentialIsConfigured(onboarding, key);

  // Azure state
  const [azTenantId, setAzTenantId] = useState('');
  const [azAdditionalTenantIds, setAzAdditionalTenantIds] = useState('');
  const [azSaving, setAzSaving] = useState(false);
  const [azChecking, setAzChecking] = useState(false);
  const [azResult, setAzResult] = useState(null);

  // AWS state
  const [awsAccountId, setAwsAccountId] = useState('');
  const [awsAccessKey, setAwsAccessKey] = useState('');
  const [awsSecretKey, setAwsSecretKey] = useState('');
  const [awsSaving, setAwsSaving] = useState(false);
  const [awsChecking, setAwsChecking] = useState(false);
  const [awsResult, setAwsResult] = useState(null);

  // AD state
  const [adDomainFqdn, setAdDomainFqdn] = useState('');
  const [adDc, setAdDc] = useState('');
  const [adUser, setAdUser] = useState('');
  const [adPass, setAdPass] = useState('');
  const [adSaving, setAdSaving] = useState(false);
  const [adResult, setAdResult] = useState(null);

  useEffect(() => {
    setAzTenantId('');
    setAzAdditionalTenantIds('');
    setAzResult(null);
    setAwsAccountId('');
    setAwsAccessKey('');
    setAwsSecretKey('');
    setAwsResult(null);
    setAdDomainFqdn('');
    setAdDc('');
    setAdUser('');
    setAdPass('');
    setAdResult(null);
  }, [clientId]);

  const saveSecrets = async (pairs, setSaving, setResult) => {
    const nonEmpty = pairs.filter(p => p.value);
    if (nonEmpty.length === 0) return;
    setSaving(true);
    setResult(null);
    try {
      for (const { key, value } of nonEmpty) {
        await api.post(`/clients/${clientId}/secrets`, { key, value });
      }
      await refetchOnboarding();
      setResult({ ok: true, msg: `${nonEmpty.length} credential${nonEmpty.length > 1 ? 's' : ''} saved successfully.` });
    } catch (err) {
      setResult({ ok: false, msg: `Failed: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const checkAzureReadiness = async () => {
    setAzChecking(true);
    setAzResult(null);
    try {
      const result = await api.post(`/clients/${clientId}/credentials/validate`, { provider: 'Azure' });
      setAzResult({
        ok: Boolean(result?.valid || result?.scanReady),
        msg: formatAzureValidationResult(result),
      });
    } catch (err) {
      setAzResult({ ok: false, msg: `Check failed: ${err.message}` });
    } finally {
      setAzChecking(false);
    }
  };

  const checkAwsAccess = async () => {
    setAwsChecking(true);
    setAwsResult(null);
    try {
      const result = await api.post(`/clients/${clientId}/credentials/validate`, { provider: 'AWS' });
      setAwsResult({
        ok: Boolean(result?.valid || result?.scanReady),
        msg: formatAwsValidationResult(result),
      });
    } catch (err) {
      setAwsResult({ ok: false, msg: `Check failed: ${err.message}` });
    } finally {
      setAwsChecking(false);
    }
  };

  if (obLoading) {
    return (
      <Shell title="Client Credentials" sub="Configure scan integration credentials">
        <Card style={{ padding: 22 }}><Skeleton height={200} /></Card>
      </Shell>
    );
  }

  return (
    <Shell title="Client Credentials" sub="Configure scan integration credentials">
      <CredSection
        title="Azure / M365 Tenant Access"
        icon={'\u2601'}
        fields={[
          { key: 'azure-tenant-id', label: 'Primary Tenant ID', value: azTenantId, onChange: setAzTenantId, configured: configured('azure'), placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
          { key: 'azure-tenant-ids', label: 'Additional Tenant IDs', value: azAdditionalTenantIds, onChange: setAzAdditionalTenantIds, configured: configured('azure'), placeholder: 'Optional, separated by commas or new lines' },
        ]}
        onSave={() => {
          const tenantIds = parseTenantIds(azTenantId, azAdditionalTenantIds);
          return saveSecrets([
            { key: 'azure-tenant-id', value: tenantIds[0] || '' },
            { key: 'azure-tenant-ids', value: tenantIds.length > 0 ? JSON.stringify(tenantIds) : '' },
          ], setAzSaving, setAzResult);
        }}
        saving={azSaving}
        result={azResult}
        readOnly={!canWrite}
        extraActions={<Button variant="sec" onClick={checkAzureReadiness} disabled={azChecking || !clientId}>{azChecking ? 'Checking...' : 'Verify Azure ARM Access'}</Button>}
      />

      <AwsOnboardingPanel
        clientId={clientId}
        configured={configured('aws')}
        readOnly={!canUseAwsOnboarding}
        onValidationResult={(result) => setAwsResult({
          ok: Boolean(result?.valid || result?.scanReady),
          msg: formatAwsValidationResult(result),
        })}
      />

      <CredSection
        title="AWS Access Key Fallback"
        icon={'\u2601'}
        collapsible
        defaultOpen={false}
        fields={[
          { key: 'aws/account-id', label: 'Account ID', value: awsAccountId, onChange: setAwsAccountId, configured: configured('aws'), placeholder: 'Your AWS Account ID', autoComplete: 'new-password', name: 'sentinel-demo-aws-account-id' },
          { key: 'aws/access-key-id', label: 'Access Key ID', value: awsAccessKey, onChange: setAwsAccessKey, configured: configured('aws'), placeholder: 'Your IAM access key', autoComplete: 'new-password', name: 'sentinel-demo-aws-access-key-id' },
          { key: 'aws/secret-access-key', label: 'Secret Access Key', value: awsSecretKey, onChange: setAwsSecretKey, configured: configured('aws'), type: 'password', placeholder: 'Secret access key', autoComplete: 'new-password', name: 'sentinel-demo-aws-secret-access-key' },
        ]}
        onSave={() => saveSecrets([
          { key: 'aws/account-id', value: awsAccountId },
          { key: 'aws/access-key-id', value: awsAccessKey },
          { key: 'aws/secret-access-key', value: awsSecretKey },
        ], setAwsSaving, setAwsResult)}
        saving={awsSaving}
        result={awsResult}
        readOnly={!canWrite}
        extraActions={<Button variant="sec" onClick={checkAwsAccess} disabled={awsChecking || awsSaving || !clientId}>{awsChecking ? 'Verifying...' : 'Verify AWS Access'}</Button>}
      />

      <CredSection
        title="Active Directory Credentials"
        icon={'\uD83D\uDD11'}
        collapsible
        defaultOpen={false}
        fields={[
          { key: 'ad/domain-fqdn', label: 'Domain FQDN', value: adDomainFqdn, onChange: setAdDomainFqdn, configured: configured('ad'), placeholder: 'corp.example.com' },
          { key: 'ad/domain-controller', label: 'Domain Controller (IP / Hostname)', value: adDc, onChange: setAdDc, configured: configured('ad'), placeholder: '192.168.1.10 or dc01.corp.example.com' },
          { key: 'ad/service-account-upn', label: 'Service Account UPN', value: adUser, onChange: setAdUser, configured: configured('ad'), placeholder: 'svc-sentinel@corp.example.com' },
          { key: 'ad/service-account-password', label: 'Service Account Password', value: adPass, onChange: setAdPass, configured: configured('ad'), type: 'password', placeholder: 'Service account password', autoComplete: 'new-password' },
        ]}
        onSave={() => saveSecrets(buildAdSecretPairs({
          domainFqdn: adDomainFqdn,
          domainController: adDc,
          serviceAccountUpn: adUser,
          serviceAccountPassword: adPass,
        }), setAdSaving, setAdResult)}
        saving={adSaving}
        result={adResult}
        readOnly={!canWrite}
      />
    </Shell>
  );
}